import { useState, type FormEvent } from 'react';
import { Loader2, Trash2 } from 'lucide-react';
import { executionApi } from './api';
import type {
  ExecutionKind,
  ExecutionNode,
  ExecutionParent,
  ExecutionSnapshot,
  NodeInput,
} from './model';
import { describeError, kindLabels, Modal, useExecutionActive } from './ExecutionShared';

const statusOptions: Record<ExecutionKind, string[]> = {
  objective: ['not_started', 'in_progress', 'completed', 'on_hold'],
  strategy: ['planned', 'in_progress', 'completed', 'on_hold'],
  project: ['planning', 'active', 'on_hold', 'completed', 'cancelled'],
  task: ['not_started', 'in_progress', 'completed', 'blocked'],
};
export interface WorkDialogRequest {
  kind: ExecutionKind;
  node?: ExecutionNode;
  parent?: ExecutionParent | null;
  parentTitle?: string;
  objectiveTitle?: string;
}
export default function ExecutionWorkDialog({
  request,
  snapshot,
  onClose,
  onSaved,
  onDelete,
}: {
  request: WorkDialogRequest;
  snapshot: ExecutionSnapshot;
  onClose: () => void;
  onSaved: (message: string) => Promise<void>;
  onDelete?: (node: ExecutionNode) => void;
}) {
  const active = useExecutionActive();
  const { node, kind } = request;
  const initialOwner = node?.owner.positionId
    ? `position:${node.owner.positionId}`
    : node?.owner.userId
      ? `user:${node.owner.userId}`
      : '';
  const [title, setTitle] = useState(node?.title ?? '');
  const [description, setDescription] = useState(node?.description ?? '');
  const [priority, setPriority] = useState(node?.priority ?? 'medium');
  const [status, setStatus] = useState(node?.status ?? statusOptions[kind][0]);
  const [owner, setOwner] = useState(initialOwner);
  const [dueDate, setDueDate] = useState(node?.dueDate?.slice(0, 10) ?? '');
  const [hours, setHours] = useState(node?.estimatedHours ? String(node.estimatedHours) : '');
  const [progressMode, setProgressMode] = useState(node?.progressMode ?? 'calculated');
  const [progress, setProgress] = useState(node?.storedProgress ?? 0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving || !snapshot.canEdit) return;
    setSaving(true);
    setError(null);
    const input: NodeInput = {
      title: title.trim(),
      description: description.trim() || null,
      priority,
      status,
      dueDate: dueDate || null,
      estimatedHours: hours ? Number(hours) : null,
      ...(kind !== 'objective' ? { progressMode, progress } : {}),
    };
    if (!node || owner !== initialOwner) {
      input.assignedTo = owner.startsWith('user:') ? owner.slice(5) : null;
      input.assignedPositionId = owner.startsWith('position:') ? owner.slice(9) : null;
    }
    try {
      if (node) {
        const patch: Partial<NodeInput> = {};
        if (input.title !== node.title) patch.title = input.title;
        if (input.description !== node.description) patch.description = input.description;
        if (input.status !== node.status) patch.status = input.status;
        if (input.priority !== node.priority) patch.priority = input.priority;
        if (dueDate !== (node.dueDate?.slice(0, 10) ?? '')) patch.dueDate = input.dueDate;
        if ((Number(hours) || 0) !== node.estimatedHours)
          patch.estimatedHours = input.estimatedHours;
        if (kind !== 'objective' && progressMode !== node.progressMode)
          patch.progressMode = progressMode;
        if (kind !== 'objective' && progress !== node.storedProgress) patch.progress = progress;
        if (owner !== initialOwner) {
          patch.assignedTo = input.assignedTo;
          patch.assignedPositionId = input.assignedPositionId;
        }
        if (Object.keys(patch).length)
          await executionApi.updateNode(snapshot.company.id, kind, node.id, patch);
      } else if (kind !== 'objective') {
        await executionApi.createNode(snapshot.company.id, kind, request.parent ?? null, input);
      }
      if (!active.current) return;
      await onSaved(`${kindLabels[kind]} ${node ? 'updated' : 'created'}.`);
      if (active.current) onClose();
    } catch (err) {
      if (active.current) setError(describeError(err));
    } finally {
      if (active.current) setSaving(false);
    }
  }
  const options = [...new Set([...statusOptions[kind], ...(node?.status ? [node.status] : [])])];
  return (
    <Modal
      title={`${node ? 'Edit' : 'Add'} ${kindLabels[kind].toLowerCase()}`}
      description={
        request.objectiveTitle
          ? `Driving: ${request.objectiveTitle}${request.parentTitle ? ` · Under: ${request.parentTitle}` : ''}`
          : 'Give this work a clear purpose, owner and target date.'
      }
      onClose={onClose}
    >
      <form onSubmit={submit}>
        <label htmlFor="execution-work-title">{kind === 'objective' ? 'Objective' : 'Title'}</label>
        <input
          id="execution-work-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={255}
          required
        />
        <label htmlFor="execution-work-description">
          {kind === 'objective'
            ? 'Why this outcome matters'
            : 'How this moves the objective forward'}
        </label>
        <textarea
          id="execution-work-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={20000}
        />
        <div className="execution-dialog-grid">
          <div>
            <label htmlFor="execution-work-owner">Responsible owner</label>
            <select
              id="execution-work-owner"
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
            >
              <option value="">Unassigned</option>
              {node &&
                initialOwner &&
                ((node.owner.positionId &&
                  !snapshot.positions.some((position) => position.id === node.owner.positionId)) ||
                  (!node.owner.positionId &&
                    node.owner.userId &&
                    !snapshot.people.some((person) => person.id === node.owner.userId))) && (
                  <option value={initialOwner}>
                    Current assignment: {node.owner.positionTitle || node.owner.name}
                  </option>
                )}
              <optgroup label="People">
                {snapshot.people.map((person) => (
                  <option key={person.id} value={`user:${person.id}`}>
                    {person.name}
                  </option>
                ))}
              </optgroup>
              {(kind === 'project' || kind === 'task') && (
                <optgroup label="Positions">
                  {snapshot.positions.map((position) => (
                    <option key={position.id} value={`position:${position.id}`}>
                      {position.title} ·{' '}
                      {position.holders.map((person) => person.name).join(', ') ||
                        'Position vacant'}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>
          <div>
            <label htmlFor="execution-work-date">Target date</label>
            <input
              id="execution-work-date"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="execution-work-status">Status</label>
            <select
              id="execution-work-status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {options.map((option) => (
                <option key={option} value={option}>
                  {option.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="execution-work-priority">Priority</label>
            <select
              id="execution-work-priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            >
              {['low', 'medium', 'high', ...(kind === 'task' ? ['critical'] : [])].map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>
        {kind !== 'objective' && (
          <>
            <div className="execution-dialog-grid">
              <div>
                <label htmlFor="execution-work-hours">Own estimated hours</label>
                <input
                  id="execution-work-hours"
                  type="number"
                  min={0}
                  max={kind === 'task' ? 999.99 : 99999}
                  step={0.01}
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="execution-work-mode">Delivery completion</label>
                <select
                  id="execution-work-mode"
                  value={progressMode}
                  onChange={(e) => setProgressMode(e.target.value as 'calculated' | 'manual')}
                >
                  <option value="calculated">Calculated from supporting work</option>
                  <option value="manual">Manual completion</option>
                </select>
              </div>
            </div>
            {progressMode === 'manual' && (
              <>
                <label htmlFor="execution-work-progress">Manual completion (%)</label>
                <input
                  id="execution-work-progress"
                  type="number"
                  min={0}
                  max={100}
                  value={progress}
                  onChange={(e) => setProgress(Number(e.target.value))}
                  required
                />
              </>
            )}
            <p className="execution-dialog-help">
              Delivery completion tracks work done. Record the objective’s outcome separately to see
              whether this work is moving the needle.
            </p>
          </>
        )}
        {error && (
          <div className="execution-notice error" role="alert">
            {error}
          </div>
        )}
        <div className="execution-dialog-footer">
          {node && (kind === 'project' || kind === 'task') && onDelete && (
            <button
              className="execution-button danger"
              type="button"
              onClick={() => onDelete(node)}
              disabled={saving}
              style={{ marginRight: 'auto' }}
            >
              <Trash2 size={13} />
              Delete {kind}
            </button>
          )}
          <button className="execution-button" type="button" onClick={onClose}>
            Cancel
          </button>
          <button
            className="execution-button primary"
            type="submit"
            disabled={saving || !snapshot.canEdit}
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            {saving ? 'Saving…' : node ? 'Save changes' : `Add ${kind}`}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function ExecutionAlignmentDialog({
  node,
  snapshot,
  onClose,
  onSaved,
}: {
  node: ExecutionNode;
  snapshot: ExecutionSnapshot;
  onClose: () => void;
  onSaved: (message: string) => Promise<void>;
}) {
  const active = useExecutionActive();
  const [objectiveId, setObjectiveId] = useState('');
  const [parentId, setParentId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const objective = snapshot.objectives.find((item) => item.id === objectiveId);
  const parents =
    node.type === 'project'
      ? (objective?.children ?? [])
      : (objective?.children.flatMap((strategy) => strategy.children) ?? []);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!parentId || saving || !snapshot.canEdit) return;
    setSaving(true);
    setError(null);
    try {
      if (node.type === 'project')
        await executionApi.alignProject(snapshot.company.id, node.id, parentId);
      else await executionApi.alignTask(snapshot.company.id, node.id, parentId);
      if (!active.current) return;
      await onSaved(`“${node.title}” is now linked to an objective.`);
      if (active.current) onClose();
    } catch (err) {
      if (active.current) setError(describeError(err));
    } finally {
      if (active.current) setSaving(false);
    }
  }
  return (
    <Modal
      title="Link work to an objective"
      description={`Give “${node.title}” a clear place in the plan. Its existing details and supporting tasks will be preserved.`}
      onClose={onClose}
    >
      <form onSubmit={submit}>
        <label htmlFor="execution-align-objective">Objective</label>
        <select
          id="execution-align-objective"
          required
          value={objectiveId}
          onChange={(e) => {
            setObjectiveId(e.target.value);
            setParentId('');
          }}
        >
          <option value="">Choose objective</option>
          {snapshot.objectives.map((item) => (
            <option key={item.id} value={item.id}>
              {item.title}
            </option>
          ))}
        </select>
        <label htmlFor="execution-align-parent">
          {node.type === 'project' ? 'Supporting strategy' : 'Supporting project'}
        </label>
        <select
          id="execution-align-parent"
          required
          value={parentId}
          onChange={(e) => setParentId(e.target.value)}
          disabled={!objectiveId}
        >
          <option value="">Choose {node.type === 'project' ? 'strategy' : 'project'}</option>
          {parents.map((parent) => (
            <option key={parent.id} value={parent.id}>
              {parent.title}
            </option>
          ))}
        </select>
        {objective && parents.length === 0 && (
          <p className="execution-dialog-help">
            Add a {node.type === 'project' ? 'strategy' : 'project'} to this objective first, then
            link the work here.
          </p>
        )}
        {error && (
          <div className="execution-notice error" role="alert">
            {error}
          </div>
        )}
        <div className="execution-dialog-footer">
          <button className="execution-button" type="button" onClick={onClose}>
            Cancel
          </button>
          <button
            className="execution-button primary"
            type="submit"
            disabled={!parentId || saving || !snapshot.canEdit}
          >
            {saving ? 'Linking…' : 'Link work'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
